import type MarkdownIt from 'markdown-it';
import type StateInline from 'markdown-it/lib/rules_inline/state_inline.mjs';

export const FORMULA_EXPERIMENT_NAME = 'rovo_chat_render_formulas' as const;
export const FORMULA_EXTENSION_TYPE = 'com.atlassian.rovo.formula' as const;
export const FORMULA_EXTENSION_KEY = 'formula' as const;

type FormulaMatch = {
	displayMode: boolean;
	end: number;
	formula: string;
};

type StateInlineWithLinkLevel = StateInline & {
	linkLevel?: number;
};

const isEscaped = (source: string, delimiterStart: number): boolean => {
	let precedingBackslashCount = 0;

	for (let index = delimiterStart - 1; index >= 0 && source[index] === '\\'; index -= 1) {
		precedingBackslashCount += 1;
	}

	return precedingBackslashCount % 2 === 1;
};

const findUnescapedDelimiter = (
	source: string,
	start: number,
	delimiter: string,
	acceptDelimiter: (delimiterStart: number) => boolean = () => true,
): number => {
	let delimiterStart = source.indexOf(delimiter, start);

	while (delimiterStart !== -1) {
		if (!isEscaped(source, delimiterStart) && acceptDelimiter(delimiterStart)) {
			return delimiterStart;
		}

		delimiterStart = source.indexOf(delimiter, delimiterStart + 1);
	}

	return -1;
};

const getFormulaMatchAt = (source: string, start: number): FormulaMatch | undefined => {
	const delimiters = [
		{ close: '$$', displayMode: true, open: '$$' },
		{ close: '\\]', displayMode: true, open: '\\[' },
		{ close: '\\)', displayMode: false, open: '\\(' },
	] as const;

	for (const delimiter of delimiters) {
		if (!source.startsWith(delimiter.open, start) || isEscaped(source, start)) {
			continue;
		}

		const closeStart = findUnescapedDelimiter(
			source,
			start + delimiter.open.length,
			delimiter.close,
		);

		if (closeStart === -1) {
			continue;
		}

		const formula = source.slice(start + delimiter.open.length, closeStart);

		if (formula.trim().length > 0) {
			return {
				end: closeStart + delimiter.close.length,
				formula,
				displayMode: delimiter.displayMode,
			};
		}
	}

	if (source[start] !== '$' || source.startsWith('$$', start) || isEscaped(source, start)) {
		return undefined;
	}

	const firstFormulaCharacter = source[start + 1];

	if (
		firstFormulaCharacter === undefined ||
		/\s/.test(firstFormulaCharacter) ||
		firstFormulaCharacter === '$'
	) {
		return undefined;
	}

	// A single-dollar opener closes at the first unescaped dollar, including
	// the first dollar in an adjacent pair ($x$$y$).
	// Require tight inner whitespace and a word boundary after the closer so
	// currency cannot consume a later word or formula opener as its closer.
	const closeStart = findUnescapedDelimiter(source, start + 1, '$');

	if (
		closeStart === -1 ||
		/\s/.test(source[closeStart - 1]) ||
		/[\p{L}\p{N}]/u.test(source[closeStart + 1] ?? '')
	) {
		return undefined;
	}

	const formula = source.slice(start + 1, closeStart);

	return formula.trim().length > 0
		? {
				end: closeStart + 1,
				formula,
				displayMode: false,
			}
		: undefined;
};

const createFormulaToken = (state: StateInline, match: FormulaMatch): void => {
	const token = state.push('formula', 'span', 0);
	token.block = false;
	token.content = state.src.slice(state.pos, match.end);
	token.attrs = [
		['extensionType', FORMULA_EXTENSION_TYPE],
		['extensionKey', FORMULA_EXTENSION_KEY],
		[
			'parameters',
			JSON.stringify({
				formula: match.formula,
				displayMode: match.displayMode,
			}),
		],
		['text', match.displayMode ? `$$${match.formula}$$` : `$${match.formula}$`],
	];
};

const formulaRule = (state: StateInline, silent: boolean): boolean => {
	const stateWithLinkLevel = state as StateInlineWithLinkLevel;

	// Formulas inside links remain ordinary Markdown content, matching the previous
	// ADF post-processing behavior. Code spans are consumed by the backtick rule before
	// this rule can inspect their contents. Formulas inside marks are intentionally
	// allowed so marked formula text is rendered while retaining its mark.
	if ((stateWithLinkLevel.linkLevel ?? 0) > 0) {
		return false;
	}

	const match = getFormulaMatchAt(state.src, state.pos);

	if (!match) {
		// Consume an unmatched display opener as ordinary text. Otherwise MarkdownIt's
		// normal text rule can advance to the second `$`, where a later `$` might be
		// incorrectly interpreted as an inline formula closer.
		if (state.src.startsWith('$$', state.pos) && !isEscaped(state.src, state.pos)) {
			if (!silent) {
				const token = state.push('text', '', 0);
				token.content = '$$';
			}

			state.pos += 2;
			return true;
		}

		return false;
	}

	if (!silent) {
		createFormulaToken(state, match);
	}

	state.pos = match.end;
	return true;
};

export const isFormulaExtension = (extensionType?: string, extensionKey?: string): boolean =>
	extensionType === FORMULA_EXTENSION_TYPE && extensionKey === FORMULA_EXTENSION_KEY;

export default function mdPluginFormula(md: MarkdownIt): void {
	// Register before MarkdownIt's escape rule so escaped LaTeX delimiters such as
	// \(...\) are matched against the original source rather than the normalized token.
	md.inline.ruler.before('escape', 'formula', formulaRule);
}
